/**
 * Before/After comparison slider (P2-T10).
 *
 * One shared component for every screen that compares two images: template
 * detail/editor preview, result page (Enhance, Retouch, Remove Object, ...).
 *
 *   html:  compareSliderHtml({ before, after, ratio })
 *   bind:  bindCompareSliders(root)   // call once after root.innerHTML = ...
 *
 * How it works: a transparent <input type="range"> covers the whole box, so the
 * browser gives us touch, mouse, pen and keyboard (←/→, Home, End, PageUp/Down)
 * for free, plus screen-reader semantics. The input only writes a CSS variable
 * (--pos); the clip + handle are driven by that variable, so a drag costs one
 * style write per frame (no layout, no re-render) and stays at 60fps.
 */
import { icon } from './icon';

export interface CompareSliderOptions {
  /** Original image URL (shown on the left). */
  before: string;
  /** Result image URL (shown on the right). */
  after: string;
  /** CSS aspect-ratio, e.g. "4 / 5" or "1024 / 768". Defaults to "4 / 5". */
  ratio?: string;
  beforeLabel?: string;
  afterLabel?: string;
  /** Accessible name of the slider (also read by screen readers). */
  ariaLabel?: string;
  /** Alt text for the two images. */
  beforeAlt?: string;
  afterAlt?: string;
  /** Initial divider position, 0–100. Default 50. */
  start?: number;
  /** Extra classes on the root, e.g. "ed-preview" for the glowing frame. */
  className?: string;
  /** Optional id for the root element. */
  id?: string;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function clampPct(n: number): number {
  return Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : 50;
}

function describe(pct: number, beforeLabel: string, afterLabel: string): string {
  if (pct <= 0) return `Showing ${afterLabel.toLowerCase()} only`;
  if (pct >= 100) return `Showing ${beforeLabel.toLowerCase()} only`;
  return `${pct}% ${beforeLabel.toLowerCase()}, ${100 - pct}% ${afterLabel.toLowerCase()}`;
}

/** Markup for one slider. Pair with `bindCompareSliders` on a parent element. */
export function compareSliderHtml(o: CompareSliderOptions): string {
  const start = clampPct(o.start ?? 50);
  const beforeLabel = o.beforeLabel ?? 'Before';
  const afterLabel = o.afterLabel ?? 'After';
  const label = o.ariaLabel ?? `Compare ${beforeLabel.toLowerCase()} and ${afterLabel.toLowerCase()}`;
  const cls = ['cmp', o.className ?? ''].filter(Boolean).join(' ');
  return `
    <div class="${cls}"${o.id ? ` id="${esc(o.id)}"` : ''} data-cmp style="--pos:${start}%;aspect-ratio:${esc(o.ratio ?? '4 / 5')}">
      <img class="cmp-after" src="${esc(o.after)}" alt="${esc(o.afterAlt ?? afterLabel)}" decoding="async" draggable="false" />
      <img class="cmp-before" src="${esc(o.before)}" alt="${esc(o.beforeAlt ?? beforeLabel)}" decoding="async" draggable="false" />
      <span class="cmp-tag is-before" aria-hidden="true">${esc(beforeLabel)}</span>
      <span class="cmp-tag is-after" aria-hidden="true">${esc(afterLabel)}</span>
      <span class="cmp-handle" aria-hidden="true">${icon('chevron_left')}${icon('chevron_right')}</span>
      <input class="cmp-range" type="range" min="0" max="100" step="1" value="${start}"
        aria-label="${esc(label)}"
        aria-valuetext="${esc(describe(start, beforeLabel, afterLabel))}"
        data-before-label="${esc(beforeLabel)}" data-after-label="${esc(afterLabel)}" />
    </div>`;
}

/** Wire every `[data-cmp]` slider under `root`. Safe to call repeatedly. */
export function bindCompareSliders(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('[data-cmp]').forEach((box) => {
    if (box.dataset.cmpBound === '1') return;
    box.dataset.cmpBound = '1';
    const range = box.querySelector<HTMLInputElement>('.cmp-range');
    if (!range) return;
    const beforeLabel = range.dataset.beforeLabel ?? 'Before';
    const afterLabel = range.dataset.afterLabel ?? 'After';

    let raf = 0;
    const apply = (): void => {
      raf = 0;
      const pct = clampPct(Number(range.value));
      box.style.setProperty('--pos', `${pct}%`);
      range.setAttribute('aria-valuetext', describe(pct, beforeLabel, afterLabel));
    };
    // Coalesce input events to one style write per animation frame.
    range.addEventListener('input', () => {
      if (!raf) raf = requestAnimationFrame(apply);
    });
    const setDrag = (on: boolean) => (): void => {
      box.classList.toggle('is-dragging', on);
    };
    range.addEventListener('pointerdown', setDrag(true));
    range.addEventListener('pointerup', setDrag(false));
    range.addEventListener('pointercancel', setDrag(false));
    range.addEventListener('blur', setDrag(false));
  });
}
